import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { Package, TrendingUp, AlertTriangle, DollarSign, RefreshCw, Download, BarChart3 } from 'lucide-react';

const COLORS = ['#0052CC', '#FF5630', '#36B37E', '#6554C0', '#FF8B00', '#00B8D9', '#8777D9', '#57D9A3', '#FFC400', '#FF7452'];

const PartsConsumptionPage = () => {
  const { selectedPlant } = useData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState('6');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { months: parseInt(months) };
      if (selectedPlant && selectedPlant !== 'all') params.plant_id = selectedPlant;
      const res = await api.get('/reports/parts-consumption/summary', { params });
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant, months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Articol', 'Cantitate', 'Valoare (RON)', 'Nr. Miscari']];
    data.top_articles_by_quantity.forEach(a => {
      rows.push([a.name, a.quantity, a.value.toFixed(2), a.count]);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `consum_piese_${months}luni.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const pieData = data.top_articles_by_value.slice(0, 8).map((a, i) => ({
    name: a.name.length > 20 ? a.name.substring(0, 20) + '...' : a.name,
    value: Math.round(a.value)
  }));

  return (
    <div className="space-y-6" data-testid="parts-consumption-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Raport Consum Piese</h1>
          <p className="text-slate-500 mt-1">Analiza detaliata a consumului de piese de schimb</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-40" data-testid="months-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Ultimele 3 luni</SelectItem>
              <SelectItem value="6">Ultimele 6 luni</SelectItem>
              <SelectItem value="12">Ultimul an</SelectItem>
              <SelectItem value="24">Ultimii 2 ani</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Package className="w-6 h-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold text-slate-900">{data.total_quantity.toLocaleString('ro-RO')}</p>
            <p className="text-xs text-slate-500">Cantitate totala</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="w-6 h-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold text-slate-900">{data.total_value.toLocaleString('ro-RO')}</p>
            <p className="text-xs text-slate-500">Valoare totala (RON)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <BarChart3 className="w-6 h-6 mx-auto text-purple-500 mb-1" />
            <p className="text-2xl font-bold text-slate-900">{data.total_movements}</p>
            <p className="text-xs text-slate-500">Miscari totale</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 mx-auto text-orange-500 mb-1" />
            <p className="text-2xl font-bold text-slate-900">{data.unique_articles}</p>
            <p className="text-xs text-slate-500">Articole unice</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Trend Lunar Consum</CardTitle></CardHeader>
          <CardContent>
            {data.monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(val) => val.toLocaleString('ro-RO')} />
                  <Legend />
                  <Area type="monotone" dataKey="quantity" name="Cantitate" stroke="#0052CC" fill="#0052CC" fillOpacity={0.15} />
                  <Area type="monotone" dataKey="value" name="Valoare (RON)" stroke="#36B37E" fill="#36B37E" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">Fara date de afisat</p>
            )}
          </CardContent>
        </Card>

        {/* Top Articles by Value (Pie) */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Top Articole dupa Valoare</CardTitle></CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={100} fill="#0052CC" dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(val) => `${val.toLocaleString('ro-RO')} RON`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-center py-12">Fara date de afisat</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Articles Table */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Top 20 Piese Consumate (dupa cantitate)</CardTitle></CardHeader>
        <CardContent>
          {data.top_articles_by_quantity.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Nicio miscare de stoc in aceasta perioada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-2 px-3">#</th>
                    <th className="text-left py-2 px-3">Articol</th>
                    <th className="text-right py-2 px-3">Cantitate</th>
                    <th className="text-right py-2 px-3">Valoare (RON)</th>
                    <th className="text-right py-2 px-3">Miscari</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_articles_by_quantity.map((a, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium text-slate-500">{i + 1}</td>
                      <td className="py-2 px-3 font-medium text-slate-900">{a.name}</td>
                      <td className="py-2 px-3 text-right">{a.quantity.toLocaleString('ro-RO')}</td>
                      <td className="py-2 px-3 text-right">{a.value.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 px-3 text-right">{a.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Equipment Consumers */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Top Echipamente Consumatoare</CardTitle></CardHeader>
        <CardContent>
          {data.top_equipment_consumers.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Nicio miscare asociata unui echipament</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, data.top_equipment_consumers.length * 35)}>
              <BarChart layout="vertical" data={data.top_equipment_consumers.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val) => `${val.toLocaleString('ro-RO')} RON`} />
                <Bar dataKey="value" name="Valoare (RON)" fill="#0052CC" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Depletion Warnings */}
      {data.depletion_warnings.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Alerte Stoc - Epuizare Previzionata
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-orange-50">
                    <th className="text-left py-2 px-3">Articol</th>
                    <th className="text-right py-2 px-3">Stoc actual</th>
                    <th className="text-right py-2 px-3">Consum mediu/luna</th>
                    <th className="text-right py-2 px-3">Zile ramase</th>
                    <th className="text-center py-2 px-3">Urgenta</th>
                  </tr>
                </thead>
                <tbody>
                  {data.depletion_warnings.map((w, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2 px-3 font-medium text-slate-900">{w.article_name}</td>
                      <td className="py-2 px-3 text-right">{w.current_stock} {w.unit}</td>
                      <td className="py-2 px-3 text-right">{w.monthly_avg_consumption} {w.unit}</td>
                      <td className="py-2 px-3 text-right font-bold">{w.days_remaining} zile</td>
                      <td className="py-2 px-3 text-center">
                        <Badge className={w.days_remaining < 15 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}>
                          {w.days_remaining < 15 ? 'CRITIC' : 'ATENTIE'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PartsConsumptionPage;
